export const RETAIL_LABELS_BLOCK_SIZE = 25;
export const RETAIL_LABELS_QUEUE_BLOCK_SIZE = 100;

const LABEL_NUMBER_REGEX = /^\d+$/;

export interface MultiLabelPackage {
  number: number;
  labelCount: number;
}

export interface RetailBoxLabelsBlock {
  zpl: string;
  total: number;
  packageCount?: number | null;
  multiLabelPackages?: MultiLabelPackage[] | null;
  from: number;
  to: number;
  warnings: string[];
}

export interface RetailLabelsRequest {
  order: string;
  from?: number;
  to?: number;
  countOnly?: boolean;
}

export interface RetailLabelsProgress {
  from: number;
  to: number;
  total: number;
  packageCount: number | null;
  warnings: string[];
}

export type FetchLabelsBlock = (from: number, to: number) => Promise<RetailBoxLabelsBlock>;

export interface LocalPrinter {
  kind: 'local';
  fetchLabels: FetchLabelsBlock;
  printZpl: (zpl: string) => Promise<void>;
}

export interface PrinterComputer {
  kind: 'printerComputer';
  pcId: number;
  sendLabels: FetchLabelsBlock;
}

export interface PrintQueue {
  kind: 'printQueue';
  pcId: number;
  sendLabels: FetchLabelsBlock;
}

export type RetailLabelsPrinter = LocalPrinter | PrinterComputer | PrintQueue;

export interface PrintRetailBoxLabelsParams {
  from: number;
  to?: number | null;
  printer: RetailLabelsPrinter;
  onProgress?: (progress: RetailLabelsProgress) => void;
  isCancelled?: () => boolean;
  blockSize?: number;
}

export interface PrintRetailBoxLabelsResult {
  total: number;
  printed: number;
  lastPrinted: number;
  warnings: string[];
  cancelled: boolean;
}

export class RetailLabelsPrintError extends Error {
  constructor(
    message: string,
    public readonly total: number,
    public readonly printed: number,
    public readonly lastPrinted: number,
    public readonly warnings: string[]
  ) {
    super(message);
    Object.setPrototypeOf(this, RetailLabelsPrintError.prototype);
  }
}

export const mergeWarnings = (current: string[], incoming: string[]) =>
  incoming.reduce((merged, warning) => (merged.includes(warning) ? merged : [...merged, warning]), current);

export const printRetailBoxLabels = async ({
  from,
  to,
  printer,
  onProgress,
  isCancelled,
  blockSize = printer.kind === 'printQueue' ? RETAIL_LABELS_QUEUE_BLOCK_SIZE : RETAIL_LABELS_BLOCK_SIZE,
}: PrintRetailBoxLabelsParams): Promise<PrintRetailBoxLabelsResult> => {
  const cancelled = () => Boolean(isCancelled && isCancelled());
  const fetchBlock = printer.kind === 'local' ? printer.fetchLabels : printer.sendLabels;
  let warnings: string[] = [];
  let total = 0;
  let printed = 0;
  let lastPrinted = from - 1;
  let next = from;
  let last = typeof to === 'number' ? to : Number.MAX_SAFE_INTEGER;
  const stop = (wasCancelled: boolean) => ({ total, printed, lastPrinted, warnings, cancelled: wasCancelled });

  try {
    while (next <= last) {
      if (cancelled()) {
        return stop(true);
      }
      const block = await fetchBlock(next, Math.min(next + blockSize - 1, last));
      total = block.total;
      if (block.total === 0 || block.to < block.from) {
        break;
      }
      warnings = mergeWarnings(warnings, block.warnings || []);
      if (onProgress) {
        onProgress({
          from: block.from,
          to: block.to,
          total: block.total,
          packageCount: typeof block.packageCount === 'number' ? block.packageCount : null,
          warnings,
        });
      }
      if (printer.kind === 'local') {
        if (cancelled()) {
          return stop(true);
        }
        await printer.printZpl(block.zpl);
      }
      printed += block.to - block.from + 1;
      lastPrinted = block.to;
      last = Math.min(last, block.total);
      next = block.to + 1;
    }
  } catch (error) {
    throw new RetailLabelsPrintError(error.message, total, printed, lastPrinted, warnings);
  }

  return stop(false);
};

export const parsePackageRange = (fromText: string, toText: string, packageCount: number | null) => {
  if (!LABEL_NUMBER_REGEX.test(fromText.trim()) || !LABEL_NUMBER_REGEX.test(toText.trim())) {
    return { error: 'Digite o número do primeiro e do último volume.' };
  }
  const from = Number(fromText);
  const to = Number(toText);
  if (from < 1) {
    return { error: 'O primeiro volume é o de número 1.' };
  }
  if (from > to) {
    return { error: 'O número inicial não pode ser maior que o final.' };
  }
  if (typeof packageCount === 'number' && to > packageCount) {
    return {
      error: `O pedido tem ${packageCount} ${packageCount === 1 ? 'volume' : 'volumes'}. Não há volume ${to}.`,
    };
  }
  return { from, to };
};

export const getMultiLabelPackages = (boxes: Array<{ samePackageAsPrevious?: boolean | null }>) => {
  const multiLabelPackages: MultiLabelPackage[] = [];
  let packageNumber = 0;
  let labelCount = 0;
  const closePackage = () => {
    if (labelCount > 1) {
      multiLabelPackages.push({ number: packageNumber, labelCount });
    }
  };
  boxes.forEach((box, index) => {
    if (index === 0 || !box.samePackageAsPrevious) {
      closePackage();
      packageNumber += 1;
      labelCount = 0;
    }
    labelCount += 1;
  });
  closePackage();
  return multiLabelPackages;
};

const getPackageLabelCount = (multiLabelPackages: MultiLabelPackage[], packageNumber: number) => {
  const multiLabelPackage = multiLabelPackages.find(item => item.number === packageNumber);
  return multiLabelPackage ? multiLabelPackage.labelCount : 1;
};

const getFirstLabel = (multiLabelPackages: MultiLabelPackage[], packageNumber: number) =>
  multiLabelPackages
    .filter(item => item.number < packageNumber)
    .reduce((label, item) => label + item.labelCount - 1, packageNumber);

export const getPackageLabelRange = (multiLabelPackages: MultiLabelPackage[], fromPackage: number, toPackage: number) => ({
  from: getFirstLabel(multiLabelPackages, fromPackage),
  to: getFirstLabel(multiLabelPackages, toPackage) + getPackageLabelCount(multiLabelPackages, toPackage) - 1,
});

export const getLabelPackage = (multiLabelPackages: MultiLabelPackage[], label: number) => {
  const sortedPackages = [...multiLabelPackages].sort((first, second) => first.number - second.number);
  let extraLabels = 0;
  for (const item of sortedPackages) {
    const firstLabel = item.number + extraLabels;
    if (label < firstLabel) {
      break;
    }
    if (label < firstLabel + item.labelCount) {
      return item.number;
    }
    extraLabels += item.labelCount - 1;
  }
  return label - extraLabels;
};
